import { parse, DATE_FORMAT } from '@eneco/common/date';
import { useFormatter } from '@eneco/i18n';
import * as yup from 'yup';

interface DeliveryDateFormFieldTextualData {
  requiredMessage?: string;
  validationMinValueMessage?: string;
  validationMaxValueMessage?: string;
}

interface DeliveryDateSchemaTextualData {
  deliveryDate?: {
    deliveryDateFormField?: { value?: DeliveryDateFormFieldTextualData };
    isUrgentFormField?: { value?: { requiredMessage?: string } };
  };
}

interface BuildDeliveryDateFormSchemaParams {
  isMoving?: boolean;
  minimumDate: Date;
  maximumDate: Date;
  minimumDays: number;
  shouldShowExtraField: boolean;
  format: ReturnType<typeof useFormatter>['format'];
  dateShort: ReturnType<typeof useFormatter>['date']['short'];
  textualData?: DeliveryDateSchemaTextualData;
}

/**
 * The delivery-date yup schema (required + min/max date range, plus the optional consideration-period
 * checkbox) — byte-identical between the eneco and Oxxio delivery date steps.
 */
export const buildDeliveryDateFormSchema = ({
  isMoving,
  minimumDate,
  maximumDate,
  minimumDays,
  shouldShowExtraField,
  format,
  dateShort,
  textualData,
}: BuildDeliveryDateFormSchemaParams) =>
  yup.object({
    deliveryDate: yup
      .string()
      .required(textualData?.deliveryDate?.deliveryDateFormField?.value?.requiredMessage)
      .test({
        message: isMoving
          ? (textualData?.deliveryDate?.deliveryDateFormField?.value?.requiredMessage ?? '')
          : format(textualData?.deliveryDate?.deliveryDateFormField?.value?.validationMinValueMessage ?? '', {
              minimumDays,
            }),
        test: currentDate => !!currentDate && parse(currentDate, DATE_FORMAT) >= minimumDate,
      })
      .test({
        message: format(textualData?.deliveryDate?.deliveryDateFormField?.value?.validationMaxValueMessage ?? '', {
          maximumDate: dateShort(maximumDate),
        }),
        test: currentDate => !!currentDate && parse(currentDate, DATE_FORMAT) <= maximumDate,
      }),

    ...(shouldShowExtraField && {
      consentConsiderationPeriod: yup
        .boolean()
        .required(textualData?.deliveryDate?.isUrgentFormField?.value?.requiredMessage)
        .test('true', textualData?.deliveryDate?.isUrgentFormField?.value?.requiredMessage ?? '', value => !!value),
    }),
  });
